import { useMutation, useQueryClient } from '@tanstack/react-query';
import { del, post } from './client';
import { keys } from './queries';
import { toast } from '../components/Toast';
import type { Alert, Member, Ok, Role, Scraper, WebhookCategory } from './types';

/* ── scraper control ───────────────────────────────────────────────────────── */

type ScraperAction = 'run-now' | 'start' | 'stop';

export function useScraperAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, action }: { name: string; action: ScraperAction }) =>
      post<Ok>(`/api/scraper/${encodeURIComponent(name)}/${action}`),
    onSuccess: (_data, { name, action }) => {
      const verb = action === 'run-now' ? 'Triggered' : action === 'start' ? 'Started' : 'Stopped';
      toast(`${verb} ${name}`, 'success');
      void qc.invalidateQueries({ queryKey: keys.scrapers });
    },
  });
}

/** Enable/disable and interval share one endpoint.
 *
 *  interval_seconds must always be sent: the handler defaults it to 300, so
 *  omitting it while toggling `enabled` would silently reset a scraper's
 *  schedule to five minutes. */
export function useUpdateScraperConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      name,
      enabled,
      interval_seconds,
    }: {
      name: string;
      enabled: boolean;
      interval_seconds: number;
    }) =>
      post<Ok>(`/api/scraper/${encodeURIComponent(name)}/config`, { enabled, interval_seconds }),
    onMutate: async ({ name, enabled, interval_seconds }) => {
      await qc.cancelQueries({ queryKey: keys.scrapers });
      const previous = qc.getQueryData<Scraper[]>(keys.scrapers);
      qc.setQueryData<Scraper[]>(keys.scrapers, (prev) =>
        prev?.map((s) => (s.name === name ? { ...s, enabled, interval_seconds } : s)),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) qc.setQueryData(keys.scrapers, context.previous);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: keys.scrapers }),
  });
}

export function useRestartScraper() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => {
      await post<Ok>(`/api/scraper/${encodeURIComponent(name)}/stop`);
      return post<Ok>(`/api/scraper/${encodeURIComponent(name)}/start`);
    },
    onSuccess: (_d, name) => {
      toast(`Restarting ${name}…`, 'success');
      void qc.invalidateQueries({ queryKey: keys.scrapers });
    },
  });
}

export function useSaveScraperSource() {
  return useMutation({
    mutationFn: ({ name, content }: { name: string; content: string }) =>
      post<Ok>(`/api/scraper/${encodeURIComponent(name)}`, { content }),
    onSuccess: (_d, { name }) => toast(`Saved ${name}.py`, 'success'),
  });
}

/* ── bulk ops ──────────────────────────────────────────────────────────────── */

export function useBulkScraperAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (action: 'start-all' | 'stop-all' | 'restart-all') =>
      post<Ok>(`/api/scrapers/${action}`),
    onSuccess: (data, action) => {
      toast(data.message ?? `${action} complete`, 'success');
      void qc.invalidateQueries({ queryKey: keys.scrapers });
    },
  });
}

export function useSetAllIntervals() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (interval_seconds: number) =>
      post<{ success: boolean; updated: number }>('/api/scrapers/set-all-interval', {
        interval_seconds,
      }),
    onSuccess: (data) => {
      toast(`Updated ${data.updated} scrapers`, 'success');
      void qc.invalidateQueries({ queryKey: keys.scrapers });
    },
  });
}

/* ── alerts ────────────────────────────────────────────────────────────────── */

/** Alerts have no ids — the server deletes by exact JSON equality (Redis LREM).
 *  The payload must be echoed back byte-for-byte, so callers must pass the
 *  object exactly as received and never a normalised copy. */
export function useDismissAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ source, payload }: { source: 'sitemap' | 'social'; payload: Alert }) =>
      post<Ok>(`/api/alerts/${source}/dismiss`, { payload }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.alerts });
      void qc.invalidateQueries({ queryKey: keys.alertStats });
    },
  });
}

/* ── members ───────────────────────────────────────────────────────────────── */

export function useAddMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ discord_id, role }: { discord_id: string; role: Exclude<Role, ''> }) =>
      post<{ success: boolean; name: string }>('/api/members/add', { discord_id, role }),
    onSuccess: (data) => {
      toast(`Added ${data.name}`, 'success');
      void qc.invalidateQueries({ queryKey: ['members'] });
    },
  });
}

export function useSetMemberRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ discord_id, role }: { discord_id: string; role: Exclude<Role, ''> }) =>
      post<Ok>(`/api/members/${encodeURIComponent(discord_id)}/role`, { role }),
    onSuccess: () => {
      toast('Role updated', 'success');
      void qc.invalidateQueries({ queryKey: ['members'] });
    },
  });
}

export function useRemoveMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (member: Member) => del<Ok>(`/api/members/${encodeURIComponent(member.discord_id)}`),
    onSuccess: (_d, member) => {
      toast(`Removed ${member.name}`, 'success');
      void qc.invalidateQueries({ queryKey: ['members'] });
    },
  });
}

/* ── webhooks / deploy ─────────────────────────────────────────────────────── */

export function useSetWebhookMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mode: 'test' | 'prod') => post<Ok>('/api/webhooks/mode', { mode }),
    onSuccess: (_d, mode) => {
      toast(`Webhooks now in ${mode} mode`, 'success');
      void qc.invalidateQueries({ queryKey: ['webhooks'] });
      void qc.invalidateQueries({ queryKey: keys.systemSummary });
    },
  });
}

export function useSaveWebhookCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { slug: string; name: string; urls: Partial<Record<'test' | 'prod', string | null>> }) =>
      post<{ success: boolean; category: WebhookCategory }>('/api/webhook-categories', data),
    onSuccess: () => {
      toast('Webhook category saved', 'success');
      void qc.invalidateQueries({ queryKey: keys.webhookCategories });
    },
  });
}

export function useReconcileScraperFleet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post<Ok>('/api/scrapers/reconcile'),
    onSuccess: () => {
      toast('Fleet reconciliation requested', 'success');
      void qc.invalidateQueries({ queryKey: keys.scrapers });
    },
  });
}

export function useSaveScraperRouting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, category_slug, overrides }: { name: string; category_slug: string; overrides: Partial<Record<'test' | 'prod', string | null>> }) =>
      post<Ok>(`/api/scraper/${encodeURIComponent(name)}/routing`, { category_slug, overrides }),
    onSuccess: (_data, values) => {
      toast('Scraper routing saved', 'success');
      void qc.invalidateQueries({ queryKey: keys.scrapers });
      void qc.invalidateQueries({ queryKey: keys.scraperRouting(values.name) });
    },
  });
}

export function useTriggerDeploy() {
  return useMutation({
    mutationFn: () => post<Ok>('/api/deploy/pull'),
    onSuccess: () => toast('Deploy started', 'success'),
    // 429 means the 60s Redis lock is held — that is not a generic failure and
    // deserves its own message.
    onError: (error: unknown) => {
      const status = (error as { status?: number })?.status;
      toast(
        status === 429
          ? 'Another deploy started in the last 60 seconds'
          : `Deploy failed: ${String((error as Error)?.message ?? error)}`,
        'error',
      );
    },
  });
}
